import { FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import _ from 'lodash'
import { Cell, ColumnDef } from '@tanstack/react-table'

import { SwitchTable } from '@components/SwitchTable'

import { FormattedColumnRestrictions, RESTRICTION_TYPE } from '@types'

import { generateQuickSearchColumns } from '../helpers/generateQuickSearchColumns'

import { PinningCell } from './PinningCell'
import { ShowCell } from './ShowCell'
import { SortCell } from './SortCell'
import { WidthCell } from './WidthCell'

const RESTRICTIONS_COLUMNS: ColumnDef<FormattedColumnRestrictions>[] = [
  {
    accessorKey: 'fieldCode',
    header: i18next.t('viewColumnsTable.id'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'fieldName',
    header: i18next.t('viewColumnsTable.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'bindType',
    header: 'Binding',
    size: 70,
  },
  {
    accessorKey: 'value',
    header: 'Value',
    meta: {
      flex: 1,
    },
  },
  {
    accessorKey: 'valueType',
    header: 'Value Type',
    meta: {
      flex: 1,
    },
  },
]

type RestrictionTableProps = {
  restrictionsDataRows: FormattedColumnRestrictions[]
  updateRestrictions: (restrictions: any[]) => Promise<any>
  viewId: number
  isHideSort?: boolean
  hiddenColumns?: string[]
}

const RestrictionTableComponent: FC<RestrictionTableProps> = ({
  restrictionsDataRows,
  updateRestrictions,
  viewId,
  hiddenColumns,
}) => {
  const { t } = useTranslation()

  const handleOrderChange = useCallback(
    (rows: FormattedColumnRestrictions[], row: FormattedColumnRestrictions, newIndex: number) => {
      if (JSON.stringify(rows) !== JSON.stringify(restrictionsDataRows)) {
        updateRestrictions(
          [
            {
              viewId,
              fieldCode: row.fieldCode,
              fieldName: row.fieldName,
              restrictionType: RESTRICTION_TYPE.ORDER,
              value: newIndex,
            },
          ],
          rows.map((row, index) => ({
            viewId,
            fieldCode: row.fieldCode,
            fieldName: row.fieldName,
            restrictionType: RESTRICTION_TYPE.ORDER,
            value: index,
          }))
        )
      }

      return rows
    },
    []
  )

  const getQSColumns = () => {
    if (!hiddenColumns?.some(item => item === 'qs')) {
      return generateQuickSearchColumns({ updateRestrictions, viewId })
    }

    return []
  }

  const getShowColumn = () => {
    if (!hiddenColumns?.some(item => item === 'show')) {
      return [
        {
          accessorKey: 'show',
          header: t('viewColumnsTable.show'),
          size: 80,
          cell: ({ cell }: { cell: Cell<FormattedColumnRestrictions, unknown> }) => (
            <ShowCell cell={cell} updateRestrictions={updateRestrictions} viewId={viewId} />
          ),
        },
      ]
    }

    return []
  }

  const getSortColumn = () => {
    if (!hiddenColumns?.some(item => item === 'sort')) {
      return [
        {
          accessorKey: 'sort',
          header: t('viewColumnsTable.sort'),
          size: 100,
          cell: ({ cell }: { cell: Cell<FormattedColumnRestrictions, unknown> }) => (
            <SortCell
              cell={cell}
              restrictionsDataRows={restrictionsDataRows}
              updateRestrictions={updateRestrictions}
              viewId={viewId}
            />
          ),
        },
      ]
    }

    return []
  }

  const getColumnToSortColumn = () => {
    if (!hiddenColumns?.some(item => item === 'columnToSort')) {
      return [
        {
          accessorKey: 'columnToSort',
          header: 'Field to Sort and Filter',
          meta: {
            flex: 1,
          },
        },
      ]
    }

    return []
  }

  const columns = useMemo(() => {
    return [
      ...getShowColumn(),
      ...getQSColumns(),
      ...RESTRICTIONS_COLUMNS,
      ...getSortColumn(),
      ...getColumnToSortColumn(),
      {
        accessorKey: 'width',
        header: t('viewColumnsTable.width'),
        size: 100,
        cell: ({ cell }: { cell: Cell<FormattedColumnRestrictions, unknown> }) => (
          <WidthCell cell={cell} updateRestrictions={updateRestrictions} viewId={viewId} />
        ),
      },
      {
        accessorKey: 'pinning',
        header: 'Pinning',
        size: 200,
        cell: ({ cell }: { cell: Cell<FormattedColumnRestrictions, unknown> }) => (
          <PinningCell cell={cell} updateRestrictions={updateRestrictions} viewId={viewId} />
        ),
      },
    ]
  }, [hiddenColumns, restrictionsDataRows, viewId, updateRestrictions, t])

  return (
    <SwitchTable<FormattedColumnRestrictions>
      disablePagination
      disableSort
      disabledVisibilityColumn
      useDragAndDrop
      columns={columns}
      rows={restrictionsDataRows}
      showVisibilityColumn={!hiddenColumns?.some(item => item === 'visibility')}
      containerSx={{
        '.MuiTableBody-root > div': {
          height: '400px !important',
        },
        '& .MuiTableCell-root': {
          display: 'flex',
          alignItems: 'center',
        },
      }}
      onDropChange={handleOrderChange}
    />
  )
}

const arePropsEqual = (prevProps: RestrictionTableProps, nextProps: RestrictionTableProps) => {
  return (
    prevProps.viewId === nextProps.viewId &&
    _.isEqual(prevProps.restrictionsDataRows, nextProps.restrictionsDataRows)
  )
}

export const RestrictionTable = memo(RestrictionTableComponent, arePropsEqual)
